import React, { useState } from 'react';
import { User, PaginatedResponse, CreditHistory } from '../../types';
import { usersApi } from '../../services/api';
import UserTasks from './UserTasks';
import TrialActivity from './TrialActivity';
import CreditHistoryComponent from './CreditHistory';
import { 
  TrashIcon, 
  PencilIcon,
  ChartBarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  ClockIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { 
  getRoleDisplayName, 
  getIncrementalId, 
  getAccessTypeDisplay, 
  canDeleteUser,
  getPaginationSizeOptions 
} from '../../utils/helpers';

interface UserListProps {
  users: PaginatedResponse<User>;
  onEdit: (user: User) => void;
  onStatusChange: (userId: number, status: string) => void;
  onDelete: (userId: number) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  currentPage: number;
  pageSize: number;
  currentUserRole?: string;
}

const UserList: React.FC<UserListProps> = ({ 
  users, 
  onEdit, 
  onStatusChange, 
  onDelete, 
  onPageChange, 
  onPageSizeChange,
  currentPage,
  pageSize,
  currentUserRole 
}) => {
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [selectedUserForTasks, setSelectedUserForTasks] = useState<User | null>(null);
  const [selectedUserForTrials, setSelectedUserForTrials] = useState<User | null>(null);
  const [selectedUserForCreditHistory, setSelectedUserForCreditHistory] = useState<User | null>(null);
  const [statusChangeConfirmation, setStatusChangeConfirmation] = useState<{
    user: User;
    newStatus: string;
  } | null>(null);
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-primary-500/10 text-primary-500 dark:text-primary-400';
      case 'disabled':
        return 'bg-gray-500/10 text-gray-500 dark:text-gray-400';
      default:
        return 'bg-gray-500/10 text-gray-500 dark:text-gray-400';
    }
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
  };

  const handleDeleteConfirm = () => {
    if (userToDelete) {
      onDelete(userToDelete.user_id);
      setUserToDelete(null);
    }
  };

  const handleStatusChangeClick = (user: User, newStatus: string) => {
    if (user.status !== newStatus) {
      setStatusChangeConfirmation({ user, newStatus });
    }
  };

  const handleStatusChangeConfirm = () => {
    if (statusChangeConfirmation) {
      onStatusChange(statusChangeConfirmation.user.user_id, statusChangeConfirmation.newStatus);
      setStatusChangeConfirmation(null);
    }
  };

  // Helper function to extract app type from credit history description
  const extractAppTypeFromDescription = (description: string): string | null => {
    if (!description) return null;
    
    // Look for patterns like "App usage: ana_ide.api_request" or "App usage: ide.code_completion"
    if (description.includes("App usage:")) {
      try {
        // Extract the part after "App usage: "
        const appPart = description.split("App usage: ")[1].split(".")[0];
        
        // Handle both "ana_ide" and "ide" formats
        let appType = appPart.startsWith("ana_") ? appPart.substring(4) : appPart;
        
        // Map to standard app types
        const appTypeMapping: { [key: string]: string } = {
          "ide": "ide",
          "security": "security", 
          "devops": "devops",
          "web": "web",
          "devops_app": "devops"
        };
        
        return appTypeMapping[appType] || null;
      } catch (error) {
        // If parsing fails, fall back to keyword matching
      }
    }
    
    // Check for other patterns in description
    const descriptionLower = description.toLowerCase();
    if (descriptionLower.includes("security")) return "security";
    if (descriptionLower.includes("devops")) return "devops";
    if (descriptionLower.includes("ide")) return "ide";
    if (descriptionLower.includes("web")) return "web";
    
    return null;
  };

  // Helper function to fetch tasks from credit history for DevOps and Security
  const fetchTasksFromCreditHistory = async (userId: number, appType: 'devops' | 'security', params: any) => {
    try {
      // Get credit history for the user
      const creditHistoryResponse = await usersApi.getUserCreditHistory(userId, 1000);
      const creditHistory = Array.isArray(creditHistoryResponse.data) 
        ? creditHistoryResponse.data 
        : creditHistoryResponse.data?.data || [];

      // Filter credit history for the specific app type
      // Only include operations where description contains "User:" (not "Organization:")
      const filteredTasks = creditHistory
        .filter((entry: CreditHistory) => {
          if (entry.operation_type !== 'deduct') return false;
          if (!entry.description || !entry.description.includes('User:')) return false;
          const entryAppType = extractAppTypeFromDescription(entry.description);
          return entryAppType === appType;
        })
        .map((entry: CreditHistory, index: number) => {
          // Convert credit history entry to task-like object
          const taskName = appType === 'devops' ? 'DevOps Operation' : 'Security Scan';
          
          // Try to extract more specific info from description
          let operationType = taskName;
          let target = entry.description;
          
          if (entry.description.includes('App usage:')) {
            const parts = entry.description.split('App usage: ')[1];
            if (parts) {
              const [appPart, ...descParts] = parts.split(' ');
              operationType = appPart.split('.')[1] || taskName;
              target = descParts.join(' ') || entry.description;
            }
          }

          return {
            id: entry.id || `${appType}_${index}`,
            operation_type: operationType,
            task_name: operationType,
            description: target,
            target: target,
            scan_type: operationType,
            credits_used: Math.abs(entry.amount),
            total_cost: Math.abs(entry.amount),
            created_at: entry.created_at,
            timestamp: entry.created_at,
            reference_id: entry.reference_id
          };
        });

      // Apply search filter if provided
      let searchFiltered = filteredTasks;
      if (params.task_name) {
        const searchTerm = params.task_name.toLowerCase();
        searchFiltered = filteredTasks.filter((task: any) => 
          task.description.toLowerCase().includes(searchTerm) ||
          task.operation_type.toLowerCase().includes(searchTerm)
        );
      }

      // Apply pagination
      const page = params.page || 1;
      const limit = params.limit || 10;
      const skip = (page - 1) * limit;
      const paginatedTasks = searchFiltered.slice(skip, skip + limit);

      // Return in the expected format
      return {
        success: true,
        data: paginatedTasks,
        meta: {
          total: searchFiltered.length,
          pages: Math.ceil(searchFiltered.length / limit),
          page: page,
          limit: limit
        }
      };
    } catch (error) {
      console.error('Error fetching tasks from credit history:', error);
      return {
        success: false,
        data: [],
        meta: {
          total: 0,
          pages: 0,
          page: 1,
          limit: params.limit || 10
        }
      };
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="overflow-x-auto custom-scrollbar">
        <table className="min-w-[1200px] w-full divide-y divide-light-border dark:divide-dark-border">
            <thead className="bg-light-surface dark:bg-dark-surface">
                  <tr>
                    <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface w-16">
                      ID
                    </th>
                    <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface w-48">
                      Name
                    </th>
                    <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface w-64">
                      Email
                    </th>
                    <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface w-32">
                      Role
                    </th>
                    <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface w-48">
                      Organizations
                    </th>
                    <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface w-40">
                      Credit Limits
                    </th>
                    <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface w-32">
                      Access Types
                    </th>
                    {currentUserRole === 'super_admin' && (
                      <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface w-40">
                        Trials
                      </th>
                    )}
                    <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface w-32">
                      Status
                    </th>
                    <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface w-24">
                      Actions
                    </th>
                  </tr>
                </thead>
            <tbody className="bg-light-surface dark:bg-dark-surface divide-y divide-light-border dark:divide-dark-border">
              {users.items.map((user, index) => (
                <tr key={user.user_id} className="hover:bg-light-hover dark:hover:bg-dark-hover transition-colors duration-200">
                  <td className="w-16 px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {getIncrementalId(users.items, index, currentUserRole)}
                  </td>
                  <td className="w-48 px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {user.first_name} {user.last_name}
                    </div>
                  </td>
                  <td className="w-64 px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">{user.email}</div>
                  </td>
                  <td className="w-32 px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {getRoleDisplayName(user.role)}
                  </td>
                  <td className="w-48 px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-2">
                      {user.org_names_list?.map((orgName: string, index: number) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-500/10 text-primary-500 dark:text-primary-400"
                        >
                          {orgName}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="w-40 px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-gray-900 dark:text-white">Limit: {user.credit_limit?.toLocaleString() || '100'}</span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">Remaining: {user.remaining_credits?.toLocaleString() || '100'}</span>
                    </div>
                  </td>
                  <td className="w-32 px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-2">
                      {(user.access_type || ['web']).map((type, index) => {
                        const accessDisplay = getAccessTypeDisplay(type, currentUserRole);
                        return (
                          <span
                            key={index}
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                              accessDisplay.isComingSoon 
                                ? 'bg-yellow-500/10 text-yellow-500 dark:text-yellow-400' 
                                : 'bg-primary-500/10 text-primary-500 dark:text-primary-400'
                            }`}
                          >
                            {accessDisplay.label}
                            {accessDisplay.isComingSoon && (
                              <span className="ml-1 text-xs opacity-75">(Soon)</span>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  {currentUserRole === 'super_admin' && (
                    <td className="w-40 px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {user.web_trial && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-500 dark:text-blue-400">
                            Web
                          </span>
                        )}
                        {user.ide_trial && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-500 dark:text-green-400">
                            IDE
                          </span>
                        )}
                        {user.security_trial && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-500 dark:text-red-400">
                            Security
                          </span>
                        )}
                        {user.devops_trial && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/10 text-yellow-500 dark:text-yellow-400">
                            DevOps
                          </span>
                        )}
                        {!user.web_trial && !user.ide_trial && !user.security_trial && !user.devops_trial && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">No trials</span>
                        )}
                      </div>
                    </td>
                  )}
                  <td className="w-32 px-6 py-4 whitespace-nowrap">
                    <select
                      value={user.status}
                      onChange={(e) => handleStatusChangeClick(user, e.target.value)}
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${getStatusColor(user.status)} bg-transparent border-none focus:ring-0 cursor-pointer`}
                    >
                      <option value="active">Active</option>
                      <option value="disabled">Disabled</option>
                    </select>
                  </td>
                  <td className="w-24 px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => onEdit(user)}
                        className="text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 transition-colors duration-200"
                        title="Edit user"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setSelectedUserForTasks(user)}
                        className="text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 transition-colors duration-200"
                        title="View tasks"
                      >
                        <ChartBarIcon className="h-5 w-5" />
                      </button>
                      {(currentUserRole === 'super_admin' || currentUserRole === 'org_admin') && (
                        <button
                          onClick={() => setSelectedUserForCreditHistory(user)}
                          className="text-green-500 hover:text-green-600 dark:text-green-400 dark:hover:text-green-300 transition-colors duration-200"
                          title="View credit history"
                        >
                          <CurrencyDollarIcon className="h-5 w-5" />
                        </button>
                      )}
                      {currentUserRole === 'super_admin' && (
                        <button
                          onClick={() => setSelectedUserForTrials(user)}
                          className="text-purple-500 hover:text-purple-600 dark:text-purple-400 dark:hover:text-purple-300 transition-colors duration-200"
                          title="View trial activity"
                        >
                          <ClockIcon className="h-5 w-5" />
                        </button>
                      )}
                      {canDeleteUser(currentUserRole) && (
                        <button
                          onClick={() => handleDeleteClick(user)}
                          className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition-colors duration-200"
                          title="Delete user"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
        </table>
      </div>

      {/* Pagination */}
      {users.total > 0 && (
        <div className="px-6 py-4 flex items-center justify-between border-t border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card">
          <div className="flex items-center gap-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Showing{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {users.items.length > 0 ? (currentPage - 1) * users.size + 1 : 0}
              </span>{' '}
              to{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {Math.min(currentPage * users.size, users.total)}
              </span>{' '}
              of{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {users.total}
              </span>{' '}
              results
            </p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Show:</span>
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="px-3 py-1 rounded-lg bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
              >
                {getPaginationSizeOptions().map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg transition-colors duration-200 ${
                currentPage === 1
                  ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-light-hover dark:hover:bg-dark-hover'
              }`}
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === users.pages}
              className={`p-2 rounded-lg transition-colors duration-200 ${
                currentPage === users.pages
                  ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-light-hover dark:hover:bg-dark-hover'
              }`}
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {userToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="relative w-full max-w-md bg-light-card dark:bg-dark-card rounded-xl shadow-lg p-6">
            <button
              onClick={() => setUserToDelete(null)}
              className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-500/10">
                <TrashIcon className="h-6 w-6 text-red-500" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                Delete User
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Are you sure you want to delete {userToDelete.first_name} {userToDelete.last_name}? This action cannot be undone.
              </p>
              <div className="mt-6 flex justify-center gap-4">
                <button
                  onClick={() => setUserToDelete(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors duration-200"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tasks Modal */}
      {selectedUserForTasks && (
        <UserTasks
          userId={selectedUserForTasks.user_id}
          onClose={() => setSelectedUserForTasks(null)}
          fetchTasks={async (userId: number, params: any) => {
            // For DevOps and Security, use credit history data instead of tasks API
            if (params.task_type === 'devops' || params.task_type === 'security') {
              return await fetchTasksFromCreditHistory(userId, params.task_type, params);
            }
            
            // For IDE and Web, use the standard tasks API
            return await usersApi.fetchUserTasks(userId, params);
          }}
        />
      )}

      {/* Trial Activity Modal */}
      {selectedUserForTrials && (
        <TrialActivity
          userId={selectedUserForTrials.user_id}
          onClose={() => setSelectedUserForTrials(null)}
        />
      )}

      {/* Credit History Modal */}
      {selectedUserForCreditHistory && (
        <CreditHistoryComponent
          userId={selectedUserForCreditHistory.user_id}
          userName={`${selectedUserForCreditHistory.first_name} ${selectedUserForCreditHistory.last_name}`}
          onClose={() => setSelectedUserForCreditHistory(null)}
        />
      )}

      {/* Status Change Confirmation Modal */}
      {statusChangeConfirmation && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="relative w-full max-w-md bg-light-card dark:bg-dark-card rounded-xl shadow-lg p-6">
            <button
              onClick={() => setStatusChangeConfirmation(null)}
              className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <div className="text-center">
              <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${
                statusChangeConfirmation.newStatus === 'active' 
                  ? 'bg-primary-500/10' 
                  : 'bg-gray-500/10'
              }`}>
                <ClockIcon className={`h-6 w-6 ${
                  statusChangeConfirmation.newStatus === 'active' 
                    ? 'text-primary-500' 
                    : 'text-gray-500'
                }`} />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                Change User Status
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Are you sure you want to change {statusChangeConfirmation.user.first_name} {statusChangeConfirmation.user.last_name}'s status from{' '}
                <span className="font-medium capitalize">{statusChangeConfirmation.user.status}</span> to{' '}
                <span className="font-medium capitalize">{statusChangeConfirmation.newStatus}</span>?
              </p>
              <div className="mt-6 flex justify-center gap-4">
                <button
                  onClick={() => setStatusChangeConfirmation(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusChangeConfirm}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors duration-200 ${
                    statusChangeConfirmation.newStatus === 'active'
                      ? 'bg-primary-500 hover:bg-primary-600'
                      : 'bg-gray-500 hover:bg-gray-600'
                  }`}
                >
                  Change Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
